import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Popover } from "@mui/material";
import { BsPencil, BsTrash } from "react-icons/bs";
import { IoClose } from "react-icons/io5";
import { LuEye } from "react-icons/lu";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import DeleteModal from "../../../../components/modals/DeleteModal";
import TableFilter from "../../../../components/table/filter/TableFilter";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import {
	TIME_REQUEST_STATUS_OPTIONS,
	TimeRequestStatus,
} from "../../../../models/time-request.dto";
import {
	getTimeRequestColumns,
	TIME_REQUEST_LABELS,
	TimeRequestKind,
	TimeRequestListMode,
} from "../data";
import { getTimeRequestListHook, useTimeRequestMutations } from "../useTimeRequestApi";
import CancelTimeRequestModal from "../modals/CancelTimeRequestModal";
import TimeRequestDetailsModal from "../modals/TimeRequestDetailsModal";
import TimeRequestFormModal from "../modals/TimeRequestFormModal";

type TimeRequestsViewProps = {
	kind: TimeRequestKind;
	mode: TimeRequestListMode;
	tableId: string;
	// Limits an HR list to one company
	companyId?: number;
};

const QUERY_OPTIONS: Record<TimeRequestListMode, object> = {
	SELF: { queryMode: "SELF" },
	APPROVALS: { awaitingMyResponse: true },
	ALL: { queryMode: "ALL" },
};

const TimeRequestsView = ({ kind, mode, tableId, companyId }: TimeRequestsViewProps) => {
	const [searchParams, setSearchParams] = useSearchParams();
	// Email links open a request with ?id=
	const linkedRequestId = Number(searchParams.get("id")) || undefined;
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [selected, setSelected] = useState<any>(null);
	const [detailsId, setDetailsId] = useState<number | undefined>(linkedRequestId);
	const [openForm, setOpenForm] = useState(false);
	const [editing, setEditing] = useState(false);
	const [openCancelModal, setOpenCancelModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const { remove } = useTimeRequestMutations(kind);
	const { reloadTableData, addNewTableData } = useManageTableWithSearchParamsData();

	const { tableAPIRef, tableData, isLoading, pageCount } =
		useManageTableWithSearchParams<any>({
			fetchHook: getTimeRequestListHook(kind),
			options: { ...QUERY_OPTIONS[mode], companyId },
			reloadDeps: [kind, mode, companyId],
		});

	useEffect(() => {
		if (linkedRequestId) setDetailsId(linkedRequestId);
	}, [linkedRequestId]);

	const closeDetails = () => {
		setDetailsId(undefined);
		if (searchParams.get("id")) {
			const params = new URLSearchParams(window.location.search);
			params.delete("id");
			setSearchParams(params);
		}
	};

	const openActions = (row: any, event: any) => {
		setSelected(row);
		setAnchorEl(event.currentTarget);
	};
	const closeActions = () => setAnchorEl(null);

	const handleDelete = () => {
		setDeleting(true);
		remove.mutate(
			{ id: selected?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	const isOwnPending = mode === "SELF" && selected?.status === TimeRequestStatus.PENDING;

	return (
		<>
			<div className="flex items-center justify-between w-full mb-6">
				<TableFilter
					fields={[
						{
							title: "",
							fieldName: "",
							fieldType: "SORT",
							options: [
								{ title: "Date Submitted", fieldName: "createdAt" },
								{ title: "Date Modified", fieldName: "modifiedAt" },
							],
						},
						...(mode === "APPROVALS" ? [] : [{
							title: "Status",
							fieldName: "status",
							fieldType: "CHECKBOX_LIST" as const,
							options: TIME_REQUEST_STATUS_OPTIONS,
						}]),
					]}
				/>
				{mode === "SELF" && (
					<OrangeButton
						title={TIME_REQUEST_LABELS[kind].submit}
						className="px-8 h-12"
						onClick={() => {
							setEditing(false);
							setOpenForm(true);
						}}
					/>
				)}
			</div>
			<TanStackEmployeeTable
				tableId={tableId}
				data={tableData}
				columns={getTimeRequestColumns(kind, mode !== "SELF")}
				loading={isLoading}
				onRowClick={openActions}
				onActionClick={openActions}
				initialColumnVisibility={{}}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			{!isLoading && tableData.length === 0 && mode === "APPROVALS" && (
				<p className="text-center text-2sm text-gray-500 mt-6">
					No requests are waiting for your response
				</p>
			)}

			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={closeActions}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				PaperProps={{ style: { width: "auto", borderRadius: 8 } }}
			>
				<div className="flex flex-col justify-center p-3 text-secondary-darkgray">
					<div
						className="table-action-popover"
						onClick={() => {
							setDetailsId(selected?.id);
							closeActions();
						}}
					>
						<LuEye />
						<p className="text-2sm">
							{selected?.canRespond ? "View & Respond" : "View Details"}
						</p>
					</div>
					{isOwnPending && (<>
						<div
							className="table-action-popover"
							onClick={() => {
								setEditing(true);
								setOpenForm(true);
								closeActions();
							}}
						>
							<BsPencil />
							<p className="text-2sm">Edit</p>
						</div>
						<div
							className="table-action-popover"
							onClick={() => {
								setOpenCancelModal(true);
								closeActions();
							}}
						>
							<IoClose className="scale-[1.2]" />
							<p className="text-2sm">Cancel</p>
						</div>
						<div
							className="table-action-popover"
							onClick={() => {
								setOpenDeleteModal(true);
								closeActions();
							}}
						>
							<BsTrash />
							<p className="text-2sm">Delete</p>
						</div>
					</>)}
				</div>
			</Popover>

			<DeleteModal
				openDeleteModal={openDeleteModal}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDelete}
				spanContent={`this ${TIME_REQUEST_LABELS[kind].name.toLowerCase()}`}
				deleting={deleting}
			/>
			{openForm && (
				<TimeRequestFormModal
					kind={kind}
					open={openForm}
					setOpen={setOpenForm}
					request={editing ? selected : undefined}
					onSaved={() => (editing ? reloadTableData() : addNewTableData())}
				/>
			)}
			{openCancelModal && (
				<CancelTimeRequestModal
					kind={kind}
					request={selected}
					open={openCancelModal}
					setOpen={setOpenCancelModal}
					onCancelled={() => reloadTableData()}
				/>
			)}
			{detailsId && (
				<TimeRequestDetailsModal
					kind={kind}
					mode={mode}
					requestId={detailsId}
					open={!!detailsId}
					setOpen={(open) => !open && closeDetails()}
					onChanged={() => reloadTableData()}
				/>
			)}
		</>
	);
};

export default TimeRequestsView;
